/**
 * Budget Service - FinList Advanced Household Budgeting
 * Implements the comprehensive budget model with envelopes, rollovers, and goal integration
 * 
 * Features:
 * - Budget periods (monthly/weekly/custom)
 * - Category envelopes with rollover rules
 * - Safe-to-spend calculations
 * - Goal-aware budget allocation
 * - Refund-correct expense tracking
 * - Client-side aggregation for privacy
 * 
 * CATEGORY ARCHITECTURE (v2.0):
 * - All category normalization uses categoryCore.js as single source of truth
 * - Local CANONICAL_CATEGORY_MAP removed - use normalizeToCategoryTaxonomyId() instead
 * - All documents should store categoryTaxonomyId (canonical ID)
 */

import { db, auth } from '@/config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { findCategoryById, getCategoryColor } from '@/constants/unifiedCategories';
import { getCategoryById as getTaxonomyCategory, CATEGORY_TYPES } from '@/constants/categoryTaxonomy';
// CATEGORY CORE: Single source of truth for category normalization
import { 
  normalizeToCategoryTaxonomyId, 
  normalizeCategoryWithFallback,
  getCategoryDisplayName,
  isCanonicalCategoryId,
  CATEGORY_INDEX,
  CANONICAL_CATEGORY_IDS
} from '@/constants/categoryCore';

// CATEGORY SYSTEM: For identifying savings vs expense categories
import { isSavingsCategory } from '@/constants/categorySystem';

// BUDGET CONSTANTS: Extracted to separate module for modularity
import { 
  BUDGET_CADENCE, 
  BUDGET_TEMPLATES, 
  BUDGET_STATUS, 
  ROLLOVER_RULES,
  DEMOGRAPHIC_TEMPLATES,
  DEMOGRAPHIC_TEMPLATE_CONFIG
} from './budgetConstants';

// BUDGET HELPERS: Pure utility functions extracted for modularity
import { 
  getCategoryLabel,
  normalizeCategoryKey,
  formatBudgetAmount,
  calculatePercentage,
  calculateBurnRate,
  getBurnRateAlert
} from './budgetHelpers';

// BUDGET ANALYTICS: Read-only analytics functions extracted for modularity
import {
  getTopIncomeSources as _getTopIncomeSources,
  getTopSpendingCategories as _getTopSpendingCategories,
  getGoalsSummary as _getGoalsSummary
} from './budgetAnalytics';

// BUDGET CRUD: Basic CRUD operations extracted for modularity
import {
  getUserBudgetsRef,
  getBudgetLinesRef,
  getBudgetSummaryRef,
  createBudget as _createBudget,
  getBudget as _getBudget,
  getUserBudgets as _getUserBudgets,
  updateBudget as _updateBudget,
  deleteBudget as _deleteBudget,
  getActiveBudget as _getActiveBudget
} from './budgetCrud';

// NOTE: These modules have been created for future migration:
// - budgetEnvelopes.js: Envelope CRUD and subscription functions
// - budgetSavings.js: Savings envelope operations  
// - budgetMetrics.js: Budget hub metrics
// The inline implementations below still remain active for now.

import logger from '@/utils/logger';

// Re-export constants for backward compatibility
export { BUDGET_CADENCE, BUDGET_TEMPLATES, BUDGET_STATUS, ROLLOVER_RULES, DEMOGRAPHIC_TEMPLATES, DEMOGRAPHIC_TEMPLATE_CONFIG };

// Re-export helpers for backward compatibility
export { normalizeCategoryKey };

// Re-export analytics for backward compatibility
export { 
  _getTopIncomeSources as getTopIncomeSources,
  _getTopSpendingCategories as getTopSpendingCategories,
  _getGoalsSummary as getGoalsSummary
};

// Re-export CRUD for backward compatibility
export {
  _createBudget as createBudget,
  _getBudget as getBudget,
  _getUserBudgets as getUserBudgets,
  _updateBudget as updateBudget,
  _deleteBudget as deleteBudget,
  _getActiveBudget as getActiveBudget
};

// NOTE: Envelope, savings, and metrics functions are now in separate modules
// but still exported directly from this file for backward compatibility.
// See: budgetEnvelopes.js, budgetSavings.js, budgetMetrics.js
// The inline implementations below will be removed in a future cleanup.

// ============================================================================
// FUNCTION LOCATIONS (for maintainability)
// ============================================================================
// budgetCrud.js: createBudget, getBudget, getUserBudgets, updateBudget, deleteBudget, getActiveBudget
// budgetEnvelopes.js: Envelope CRUD and subscription functions
// budgetSavings.js: Savings envelope operations
// budgetMetrics.js: Budget hub metrics
// budgetHelpers.js: getCategoryLabel, normalizeCategoryKey, formatBudgetAmount, etc.
// budgetAnalytics.js: getTopIncomeSources, getTopSpendingCategories, getGoalsSummary
// budgetConstants.js: BUDGET_CADENCE, BUDGET_TEMPLATES, BUDGET_STATUS, ROLLOVER_RULES

// ============================================================================
// CANONICAL ENVELOPE HELPERS (Phase 2)
// These helpers provide a single source of truth for budget envelopes.
// Other modules should use these to READ envelopes - ONLY BudgetPage can CREATE.
// ============================================================================

/**
 * Get all canonical envelopes for the active budget period
 * This is the ONE API that other modules should use to get envelope information.
 * 
 * @param {string} userId - User ID
 * @param {string} budgetId - Active budget ID
 * @returns {Promise<Array>} Array of normalized envelope objects
 */
export async function getCanonicalEnvelopes(userId, budgetId) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');

    const linesRef = getBudgetLinesRef(userId, budgetId);
    const linesSnapshot = await getDocs(linesRef);
    
    // Map to normalized shape with both legacy and canonical property names
    // for backward compatibility with existing consumers
    const envelopes = linesSnapshot.docs.map(doc => {
      const data = doc.data();
      // FINLIST SSOT FIX: Use linkedCanonicalCategory if available (consistent with subscribeToEnvelopeChanges)
      const categoryId = data.linkedCanonicalCategory || data.categoryId; // e.g. "housing-rent" or "cat_housing"
      const envelopeLabel = getCategoryLabel(categoryId);
      return {
        // Canonical names (preferred - used by CategoryEnvelopeContext)
        id: doc.id,
        categoryId: categoryId,
        name: envelopeLabel,
        // Legacy names (for backward compatibility with existing code)
        envelopeId: doc.id,
        categoryKey: data.categoryId, // Original stored value for matching
        linkedCanonicalCategory: data.linkedCanonicalCategory, // Explicit link
        label: envelopeLabel,
        // Common fields
        budgetedAmount: Number(data.planned) || 0,
        actualSpent: Number(data.actual) || 0,
        refunds: Number(data.refunds) || 0,
        remaining: Number(data.remaining) || 0,
        periodId: budgetId,
        notes: data.notes || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      };
    });

    if (import.meta.env.DEV) {
      logger.debug('[BUDGET SERVICE] Canonical envelopes loaded:', envelopes.length);
    }

    return envelopes;
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error fetching canonical envelopes:', error);
    return [];
  }
}

/**
 * Find envelope by category key from canonical source
 * Use this to check if an envelope exists for a category BEFORE creating transactions
 * 
 * @param {string} userId - User ID
 * @param {string} budgetId - Active budget ID
 * @param {string} categoryKey - Category key (e.g. "groceries-food", "utilities-bills")
 * @returns {Promise<object|null>} Envelope object or null if not found
 */
export async function findEnvelopeByCategory(userId, budgetId, categoryKey) {
  try {
    if (!userId || !budgetId || !categoryKey) return null;
    
    const envelopes = await getCanonicalEnvelopes(userId, budgetId);
    
    // Direct match
    let envelope = envelopes.find(env => env.categoryKey === categoryKey);
    
    // If not found, try normalized category mapping
    if (!envelope) {
      const normalizedKey = normalizeCategoryKey(categoryKey);
      envelope = envelopes.find(env => 
        env.categoryKey === normalizedKey || 
        normalizeCategoryKey(env.categoryKey) === normalizedKey
      );
    }
    
    return envelope || null;
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error finding envelope by category:', error);
    return null;
  }
}

// ============================================================================
// CATEGORY NORMALIZATION - NOW USES categoryCore.js (Single Source of Truth)
// ============================================================================
// 
// LEGACY NOTE: The old normalizeCategoryKey() and CANONICAL_CATEGORY_MAP have been
// removed and consolidated into @/constants/categoryCore.js
// 
// Use the following imported functions instead:
// - normalizeToCategoryTaxonomyId(input) → returns canonical ID or null
// - normalizeCategoryWithFallback(input) → returns canonical ID or 'miscellaneous'
// - isCanonicalCategoryId(id) → checks if ID is valid canonical
// - getCategoryDisplayName(id) → gets human-readable name
// 
// DO NOT recreate local category maps - all mappings live in categoryCore.js
//
// getCategoryLabel() and normalizeCategoryKey() are now imported from budgetHelpers.js
// ============================================================================

/**
 * Check if an envelope exists for a category in the active budget
 * Use this from Shopping/Transactions/Tasks before linking to budget
 * 
 * @param {string} userId - User ID  
 * @param {string} budgetId - Active budget ID
 * @param {string} categoryKey - Category key to check
 * @returns {Promise<boolean>} True if envelope exists
 */
export async function hasEnvelopeForCategory(userId, budgetId, categoryKey) {
  const envelope = await findEnvelopeByCategory(userId, budgetId, categoryKey);
  return envelope !== null;
}

// ============================================================================
// FINLIST SSOT: Enhanced Envelope Functions (Category-Envelope Atomic Wiring)
// ============================================================================

/**
 * Subscribe to envelope changes in real-time
 * FINLIST SSOT: Use this for real-time envelope updates in CategoryEnvelopeContext
 * 
 * @param {string} userId - User ID
 * @param {Function} onUpdate - Callback with updated envelopes array
 * @param {string} budgetId - Optional budget ID (uses active budget if not provided)
 * @returns {Function} Unsubscribe function
 */
export function subscribeToEnvelopeChanges(userId, onUpdate, budgetId = null) {
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  if (!userId) {
    logger.warn('[BUDGET SERVICE SSOT] Cannot subscribe without user ID');
    return () => {};
  }

  let unsubscribe = () => {};
  let isActive = true; // Track if subscription is still active

  // First, we need to get the active budget ID
  const initSubscription = async () => {
    try {
      let activeBudgetId = budgetId;
      
      if (!activeBudgetId) {
        // Get the active budget
        logger.debug('[BUDGET SERVICE SSOT] No budgetId provided, fetching active budget...');
        const budget = await _getActiveBudget(userId);
        activeBudgetId = budget?.id;
        logger.debug('[BUDGET SERVICE SSOT] Active budget result:', activeBudgetId || 'NOT FOUND');
      }
      
      if (!activeBudgetId) {
        logger.warn('[BUDGET SERVICE SSOT] No active budget found - envelope subscription not created');
        onUpdate([]);
        return;
      }

      // Check if subscription was cancelled while we were awaiting
      if (!isActive) {
        logger.debug('[BUDGET SERVICE SSOT] Subscription cancelled during init');
        return;
      }

      logger.debug('[BUDGET SERVICE SSOT] Setting up envelope subscription for budget:', activeBudgetId);

      // Subscribe to the budget lines (envelopes)
      const linesRef = getBudgetLinesRef(userId, activeBudgetId);
      const { onSnapshot } = await import('firebase/firestore');
      
      unsubscribe = onSnapshot(linesRef, (snapshot) => {
        logger.debug('[BUDGET SERVICE SSOT] Snapshot received, docs count:', snapshot.docs.length);
        const envelopes = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            envelopeId: doc.id,
            budgetId: activeBudgetId,
            categoryKey: data.categoryId,
            categoryId: data.linkedCanonicalCategory || data.categoryId, // SSOT: Link to Category Registry
            label: getCategoryLabel(data.categoryId),
            name: getCategoryLabel(data.categoryId),
            budgetedAmount: Number(data.planned) || 0,
            planned: Number(data.planned) || 0,
            actualSpent: Number(data.actual) || 0,
            spent: Number(data.actual) || 0,
            refunds: Number(data.refunds) || 0,
            remaining: Number(data.remaining) || 0,
            carryIn: Number(data.carryIn) || 0,
            carryOut: Number(data.carryOut) || 0,
            goalLink: data.goalLink || null,
            notes: data.notes || '',
            isCustomCategory: data.isCustomCategory || false,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt
          };
        });
        
        logger.debug('[BUDGET SERVICE SSOT] Envelope subscription update:', envelopes.length, 'envelopes');
        onUpdate(envelopes);
      }, (error) => {
        logger.error('[BUDGET SERVICE SSOT] Envelope subscription error:', error);
        onUpdate([]);
      });
      
      logger.debug('[BUDGET SERVICE SSOT] Envelope subscription active for budget:', activeBudgetId);
    } catch (error) {
      logger.error('[BUDGET SERVICE SSOT] Error initializing envelope subscription:', error);
      onUpdate([]);
    }
  };

  initSubscription();
  
  // Return a function that will clean up when called
  return () => {
    isActive = false;
    unsubscribe();
  };
}

/**
 * Create an envelope for a category from the Category Registry
 * FINLIST SSOT: Only this function should create envelopes for new categories
 * 
 * @param {string} userId - User ID
 * @param {object} category - Category object from Category Registry
 * @param {string} budgetId - Optional budget ID (uses active budget if not provided)
 * @returns {Promise<object>} Created envelope
 */
export async function createEnvelopeForCategory(userId, category, budgetId = null) {
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  try {
    if (!userId) throw new Error('[BUDGET SERVICE SSOT] User ID required');
    if (!category?.id) throw new Error('[BUDGET SERVICE SSOT] Category with ID required');

    let activeBudgetId = budgetId;
    
    if (!activeBudgetId) {
      const budget = await _getActiveBudget(userId);
      activeBudgetId = budget?.id;
      
      if (!activeBudgetId) {
        throw new Error('[BUDGET SERVICE SSOT] No active budget found');
      }
    }

    // Check if envelope already exists for this category
    const existingEnvelope = await findEnvelopeByCategoryId(userId, activeBudgetId, category.id);
    if (existingEnvelope) {
      logger.debug(`[BUDGET SERVICE SSOT] Envelope already exists for category ${category.id}`);
      return existingEnvelope;
    }

    // Create new envelope
    const linesRef = getBudgetLinesRef(userId, activeBudgetId);
    const timestamp = Timestamp.now();

    const envelope = {
      categoryId: category.slug || category.id,
      linkedCanonicalCategory: category.id, // SSOT: Direct link to Category Registry
      planned: 0,
      actual: 0,
      refunds: 0,
      remaining: 0,
      carryIn: 0,
      carryOut: 0,
      goalLink: null,
      notes: '',
      isCustomCategory: false,
      createdAt: timestamp,
      updatedAt: timestamp
    };

    const { addDoc } = await import('firebase/firestore');
    const docRef = await addDoc(linesRef, envelope);
    
    logger.debug(`[BUDGET SERVICE SSOT] Created envelope ${docRef.id} for category ${category.id}`);

    return {
      id: docRef.id,
      envelopeId: docRef.id,
      budgetId: activeBudgetId,
      ...envelope,
      label: category.name,
      name: category.name
    };
  } catch (error) {
    logger.error('[BUDGET SERVICE SSOT] Error creating envelope for category:', error);
    throw error;
  }
}

/**
 * Find envelope by Category Registry ID
 * FINLIST SSOT: Use this for lookups using canonical category IDs (cat_xxxx)
 * 
 * @param {string} userId - User ID
 * @param {string} budgetId - Budget ID
 * @param {string} categoryId - Category Registry ID (e.g., "cat_groceries")
 * @returns {Promise<object|null>} Envelope object or null
 */
export async function findEnvelopeByCategoryId(userId, budgetId, categoryId) {
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  try {
    if (!userId || !budgetId || !categoryId) return null;

    const linesRef = getBudgetLinesRef(userId, budgetId);
    const { getDocs, query, where } = await import('firebase/firestore');
    
    // CRITICAL FIX: Try multiple category ID formats to handle mismatches between
    // how transactions store categoryId (cat_groceries) and how envelopes store
    // linkedCanonicalCategory (groceries-food via normalizeCategoryKey)
    const categoryIdsToTry = [
      categoryId,                           // Original: cat_groceries
      normalizeCategoryKey(categoryId),     // Normalized: groceries-food
    ];
    
    // Also add lowercase variants for robustness
    if (categoryId.toLowerCase() !== categoryId) {
      categoryIdsToTry.push(categoryId.toLowerCase());
    }
    
    logger.debug('[BUDGET SERVICE SSOT] Looking for envelope with categoryIds:', categoryIdsToTry);
    
    // Try each category ID variant
    for (const catId of categoryIdsToTry) {
      const q = query(linesRef, where('linkedCanonicalCategory', '==', catId));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const data = doc.data();
        logger.debug('[BUDGET SERVICE SSOT] Found envelope match with categoryId:', catId);
        return {
          id: doc.id,
          envelopeId: doc.id,
          budgetId,
          categoryId: data.linkedCanonicalCategory || data.categoryId,
          categoryKey: data.categoryId,
          label: getCategoryLabel(data.categoryId),
          name: getCategoryLabel(data.categoryId),
          budgetedAmount: Number(data.planned) || 0,
          spent: Number(data.actual) || 0,
          refunds: Number(data.refunds) || 0,
          remaining: Number(data.remaining) || 0,
          ...data
        };
      }
    }
    
    // Also try matching by categoryId field directly (fallback for legacy envelopes)
    for (const catId of categoryIdsToTry) {
      const q = query(linesRef, where('categoryId', '==', catId));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const data = doc.data();
        logger.debug('[BUDGET SERVICE SSOT] Found envelope match by categoryId field:', catId);
        return {
          id: doc.id,
          envelopeId: doc.id,
          budgetId,
          categoryId: data.linkedCanonicalCategory || data.categoryId,
          categoryKey: data.categoryId,
          label: getCategoryLabel(data.categoryId),
          name: getCategoryLabel(data.categoryId),
          budgetedAmount: Number(data.planned) || 0,
          spent: Number(data.actual) || 0,
          refunds: Number(data.refunds) || 0,
          remaining: Number(data.remaining) || 0,
          ...data
        };
      }
    }

    logger.debug('[BUDGET SERVICE SSOT] No envelope found for any categoryId variant:', categoryIdsToTry);
    return null;
  } catch (error) {
    logger.error('[BUDGET SERVICE SSOT] Error finding envelope by category ID:', error);
    return null;
  }
}

/**
 * Update envelope spent amount atomically
 * FINLIST SSOT: Backend should call this, not client directly
 * This is for cases where backend atomic update isn't available
 * 
 * @param {string} userId - User ID
 * @param {string} budgetId - Budget ID
 * @param {string} envelopeId - Envelope ID
 * @param {number} amountDelta - Amount to add (positive) or subtract (negative)
 * @returns {Promise<boolean>} Success status
 */
export async function updateEnvelopeSpent(userId, budgetId, envelopeId, amountDelta) {
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  try {
    if (!userId || !budgetId || !envelopeId) {
      throw new Error('[BUDGET SERVICE SSOT] Missing required parameters');
    }

    const { doc, updateDoc, increment, Timestamp } = await import('firebase/firestore');
    const envelopeRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', envelopeId);
    
    await updateDoc(envelopeRef, {
      actual: increment(amountDelta),
      remaining: increment(-amountDelta),
      updatedAt: Timestamp.now()
    });

    logger.debug(`[BUDGET SERVICE SSOT] Updated envelope ${envelopeId} spent by ${amountDelta}`);
    
    // Recalculate budget summary
    await recalcBudgetSummary(userId, budgetId);
    
    return true;
  } catch (error) {
    logger.error('[BUDGET SERVICE SSOT] Error updating envelope spent:', error);
    return false;
  }
}

/**
 * Update envelope refund amount atomically
 * FINLIST SSOT: For refund transactions - backend should call this
 * 
 * @param {string} userId - User ID
 * @param {string} budgetId - Budget ID
 * @param {string} envelopeId - Envelope ID
 * @param {number} refundAmount - Refund amount (positive value)
 * @returns {Promise<boolean>} Success status
 */
export async function updateEnvelopeRefund(userId, budgetId, envelopeId, refundAmount) {
  // FINLIST SSOT: unified category-envelope atomic wiring (global-scale perf target)
  try {
    if (!userId || !budgetId || !envelopeId) {
      throw new Error('[BUDGET SERVICE SSOT] Missing required parameters');
    }

    const { doc, updateDoc, increment, Timestamp } = await import('firebase/firestore');
    const envelopeRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', envelopeId);
    
    await updateDoc(envelopeRef, {
      refunds: increment(refundAmount),
      actual: increment(-refundAmount), // Refund reduces actual spent
      remaining: increment(refundAmount), // Refund increases remaining
      updatedAt: Timestamp.now()
    });

    logger.debug(`[BUDGET SERVICE SSOT] Updated envelope ${envelopeId} refund by ${refundAmount}`);
    
    // Recalculate budget summary
    await recalcBudgetSummary(userId, budgetId);
    
    return true;
  } catch (error) {
    logger.error('[BUDGET SERVICE SSOT] Error updating envelope refund:', error);
    return false;
  }
}

// ============================================================================
// END CANONICAL ENVELOPE HELPERS
// ============================================================================

// ============================================================================
// ENVELOPE ALLOCATION TRANSFER
// ============================================================================

/**
 * Transfer allocation between envelopes atomically
 * FINLIST SSOT: Moves planned budget from one envelope to another
 * 
 * Key Invariant: Total allocations remain constant
 *   fromEnvelope.planned -= amount
 *   toEnvelope.planned += amount
 *   Sum of all planned = unchanged
 *   Therefore Safe to Spend = unchanged
 * 
 * @param {string} userId - User ID
 * @param {string} budgetId - Budget ID
 * @param {string} fromEnvelopeId - Source envelope ID
 * @param {string} toEnvelopeId - Destination envelope ID
 * @param {number} amount - Amount to transfer (must be positive)
 * @param {string} [reason] - Optional reason for the transfer
 * @returns {Promise<{success: boolean, error?: string, transfer?: object}>}
 */
export async function transferAllocation(userId, budgetId, fromEnvelopeId, toEnvelopeId, amount, reason = '') {
  try {
    // Validation
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');
    if (!fromEnvelopeId) throw new Error('Source envelope ID required');
    if (!toEnvelopeId) throw new Error('Destination envelope ID required');
    if (fromEnvelopeId === toEnvelopeId) throw new Error('Cannot transfer to the same envelope');
    if (typeof amount !== 'number' || amount <= 0) throw new Error('Amount must be a positive number');

    // Round to 2 decimal places
    const transferAmount = Math.round(amount * 100) / 100;

    // Get envelope references
    const fromRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', fromEnvelopeId);
    const toRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', toEnvelopeId);

    // Read current values
    const [fromSnap, toSnap] = await Promise.all([getDoc(fromRef), getDoc(toRef)]);
    
    if (!fromSnap.exists()) {
      throw new Error('Source envelope not found');
    }
    if (!toSnap.exists()) {
      throw new Error('Destination envelope not found');
    }

    const fromData = fromSnap.data();
    const toData = toSnap.data();

    // Check source has enough planned allocation
    const fromPlanned = Number(fromData.planned) || 0;
    const fromActual = Number(fromData.actual) || 0;
    const fromRefunds = Number(fromData.refunds) || 0;
    
    // Available to transfer = planned - (actual - refunds) = remaining + unspent buffer
    // But we're moving planned allocation, not actual cash
    // So we check: can't transfer more than what's planned
    if (transferAmount > fromPlanned) {
      throw new Error(`Cannot transfer ${transferAmount}. Source envelope only has ${fromPlanned} planned.`);
    }

    // Use writeBatch for atomic update
    const batch = writeBatch(db);
    const timestamp = Timestamp.now();

    // Update source envelope: reduce planned, recalc remaining
    const newFromPlanned = Math.round((fromPlanned - transferAmount) * 100) / 100;
    const newFromRemaining = Math.round((newFromPlanned - fromActual + fromRefunds) * 100) / 100;
    
    batch.update(fromRef, {
      planned: newFromPlanned,
      remaining: newFromRemaining,
      updatedAt: timestamp
    });

    // Update destination envelope: increase planned, recalc remaining
    const toPlanned = Number(toData.planned) || 0;
    const toActual = Number(toData.actual) || 0;
    const toRefunds = Number(toData.refunds) || 0;
    
    const newToPlanned = Math.round((toPlanned + transferAmount) * 100) / 100;
    const newToRemaining = Math.round((newToPlanned - toActual + toRefunds) * 100) / 100;
    
    batch.update(toRef, {
      planned: newToPlanned,
      remaining: newToRemaining,
      updatedAt: timestamp
    });

    // Commit the batch
    await batch.commit();

    // Log the transfer details
    const transfer = {
      id: `transfer_${Date.now()}`,
      fromEnvelopeId,
      toEnvelopeId,
      fromCategoryId: fromData.categoryId,
      toCategoryId: toData.categoryId,
      amount: transferAmount,
      reason,
      timestamp: new Date().toISOString(),
      before: {
        from: { planned: fromPlanned, remaining: fromData.remaining },
        to: { planned: toPlanned, remaining: toData.remaining }
      },
      after: {
        from: { planned: newFromPlanned, remaining: newFromRemaining },
        to: { planned: newToPlanned, remaining: newToRemaining }
      }
    };

    logger.debug('[BUDGET SERVICE] Allocation transferred:', transfer);

    // Recalculate budget summary (updates safeToSpend, alerts, etc.)
    await recalcBudgetSummary(userId, budgetId);

    return { success: true, transfer };
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error transferring allocation:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get available transfer amount from an envelope
 * Returns how much can be transferred out (planned - spent)
 * 
 * @param {string} userId
 * @param {string} budgetId
 * @param {string} envelopeId
 * @returns {Promise<{available: number, planned: number, spent: number}>}
 */
export async function getTransferableAmount(userId, budgetId, envelopeId) {
  try {
    const envelopeRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', envelopeId);
    const snap = await getDoc(envelopeRef);
    
    if (!snap.exists()) {
      return { available: 0, planned: 0, spent: 0 };
    }

    const data = snap.data();
    const planned = Number(data.planned) || 0;
    const actual = Number(data.actual) || 0;
    const refunds = Number(data.refunds) || 0;
    const netSpent = actual - refunds;

    // Available to transfer = what's planned minus what's already spent
    // Can't transfer money that's already been spent
    const available = Math.max(0, planned - netSpent);

    return {
      available: Math.round(available * 100) / 100,
      planned: Math.round(planned * 100) / 100,
      spent: Math.round(netSpent * 100) / 100
    };
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error getting transferable amount:', error);
    return { available: 0, planned: 0, spent: 0 };
  }
}

/**
 * Add a budget line (envelope)
 * @param {string} userId 
 * @param {string} budgetId 
 * @param {object} lineData 
 * @param {object} options - Optional settings
 * @param {boolean} options.skipRecalc - Skip summary recalculation (for bulk adds)
 * @returns {Promise<object>}
 */
export async function addBudgetLine(userId, budgetId, lineData, options = {}) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');
    if (!lineData?.categoryId) throw new Error('Category ID required');

    const linesRef = getBudgetLinesRef(userId, budgetId);
    const timestamp = Timestamp.now();

    // CRITICAL FIX: Store BOTH the original categoryId AND the normalized version
    // This ensures transactions can match whether they use cat_groceries or groceries-food
    const originalCategoryId = lineData.categoryId;
    const normalizedCategoryId = normalizeToCategoryTaxonomyId(lineData.categoryId);
    
    // Determine if this is a custom category FIRST (before duplicate check)
    // Custom categories are explicitly marked OR use custom- prefix OR aren't in canonical system
    const isCustomCategory = lineData.isCustomCategory === true || 
                             originalCategoryId.startsWith('custom-') ||
                             (!isCanonicalCategoryId(normalizedCategoryId) && !originalCategoryId.startsWith('cat_'));

    // =========================================================================
    // DUPLICATE CHECK: Prevent creating duplicate envelopes for the same category
    // For custom categories, only check for EXACT categoryId match (not normalized)
    // =========================================================================
    let existingEnvelope = null;
    
    if (isCustomCategory) {
      // For custom categories, only check exact match to allow custom envelopes
      // that might normalize to the same canonical category
      const envelopes = await getCanonicalEnvelopes(userId, budgetId);
      existingEnvelope = envelopes.find(env => env.categoryKey === originalCategoryId);
    } else {
      // For canonical categories, use full matching (includes normalization)
      existingEnvelope = await findEnvelopeByCategory(userId, budgetId, originalCategoryId);
    }
    
    if (existingEnvelope) {
      const categoryInfo = CATEGORY_INDEX[normalizedCategoryId] || CATEGORY_INDEX[originalCategoryId] || {};
      const categoryLabel = categoryInfo.label || categoryInfo.name || lineData.customName || originalCategoryId || 'this category';
      const error = new Error(`An envelope for "${categoryLabel}" already exists in this budget period. You can edit the existing envelope instead.`);
      error.code = 'DUPLICATE_ENVELOPE';
      error.existingEnvelopeId = existingEnvelope.id;
      throw error;
    }

    logger.debug('[BUDGET SERVICE] Adding budget line:', {
      originalCategoryId,
      normalizedCategoryId,
      isCustomCategory
    });

    // =========================================================================
    // SAVINGS vs EXPENSE CLASSIFICATION
    // Savings envelopes work differently - they track allocations, not spending
    // =========================================================================
    const isSavingsEnvelope = isSavingsCategory(normalizedCategoryId) || 
                              isSavingsCategory(originalCategoryId);
    
    const line = {
      categoryId: originalCategoryId,
      // CRITICAL: Store the original cat_ ID as linkedCanonicalCategory 
      // since transactions use cat_groceries format
      linkedCanonicalCategory: originalCategoryId,
      // Also store the normalized version for legacy compatibility
      normalizedCategoryId: normalizedCategoryId,
      // For custom categories, store the display name
      customName: lineData.customName || null,
      planned: lineData.planned || 0,
      actual: 0,
      refunds: 0,
      remaining: lineData.planned || 0,
      carryIn: lineData.carryIn || 0,
      carryOut: 0,
      goalLink: lineData.goalLink || null,
      // =========================================================================
      // SAVINGS ENVELOPE FIELDS (CFO Money Flow Model)
      // =========================================================================
      isSavingsEnvelope: isSavingsEnvelope,       // True if this is a savings allocation
      linkedGoalId: lineData.linkedGoalId || null, // Link to Savings Goal for tracking
      fundedThisMonth: 0,                          // Amount contributed this period
      notes: lineData.notes || '',
      isCustomCategory: isCustomCategory,
      createdAt: timestamp,
      updatedAt: timestamp
    };

    const docRef = await addDoc(linesRef, line);
    logger.debug('[BUDGET SERVICE] Budget line added:', docRef.id);

    // Recalculate summary (skip if bulk adding)
    if (!options.skipRecalc) {
      await recalcBudgetSummary(userId, budgetId);
    }

    return { id: docRef.id, ...line };
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error adding budget line:', error);
    throw error;
  }
}

/**
 * Update a budget line
 * @param {string} userId 
 * @param {string} budgetId 
 * @param {string} lineId 
 * @param {object} updates 
 * @returns {Promise<object>}
 */
export async function updateBudgetLine(userId, budgetId, lineId, updates) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');
    if (!lineId) throw new Error('Line ID required');

    const lineRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', lineId);
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    };

    await updateDoc(lineRef, updateData);

    // Recalculate summary
    await recalcBudgetSummary(userId, budgetId);

    return { id: lineId, ...updateData };
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error updating budget line:', error);
    throw error;
  }
}

/**
 * Delete a budget line
 * @param {string} userId 
 * @param {string} budgetId 
 * @param {string} lineId 
 * @returns {Promise<void>}
 */
export async function deleteBudgetLine(userId, budgetId, lineId) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');
    if (!lineId) throw new Error('Line ID required');

    const lineRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', lineId);
    await deleteDoc(lineRef);

    // Recalculate summary
    await recalcBudgetSummary(userId, budgetId);

    logger.debug('[BUDGET SERVICE] Budget line deleted:', lineId);
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error deleting budget line:', error);
    throw error;
  }
}

/**
 * Recalculate budget summary
 * Client-side aggregation for privacy and offline capability
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {Promise<object>}
 */
export async function recalcBudgetSummary(userId, budgetId) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');

    // Use the canonical normalizeCategoryKey for consistent category mapping
    // This ensures all transactions map to the same envelope regardless of source

    // Get budget details
    const budget = await _getBudget(userId, budgetId);
    if (!budget) throw new Error('Budget not found');

    const lines = budget.lines || [];
    
    // FINLIST FIX: Check if this is a custom budget with stored income/budget values
    const isCustomBudget = budget.template === 'CUSTOM';
    const storedMonthlyIncome = budget.monthlyIncome || 0;
    const storedBudgetAmount = budget.customBudgetAmount || 0;
    
    // Get budget date range
    const startDate = budget.startDate?.toDate ? budget.startDate.toDate() : new Date(budget.startDate);
    const endDate = budget.endDate?.toDate ? budget.endDate.toDate() : new Date(budget.endDate);
    
    // FINLIST FIX: Calculate income lookback start date
    // For income, look back to the earlier of: budget start OR start of current calendar month
    // This ensures income added "today" is always counted even if budget starts later
    const currentDate = new Date();
    const startOfCurrentMonth = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1));
    const incomeStartDate = startDate < startOfCurrentMonth ? startDate : startOfCurrentMonth;
    
    // Helper to convert date to JS Date
    const toJSDate = (dateVal) => {
      if (!dateVal) return null;
      if (dateVal.toDate) return dateVal.toDate();
      if (typeof dateVal === 'string') return new Date(dateVal);
      if (dateVal instanceof Date) return dateVal;
      return null;
    };
    
    // Query ALL transactions and filter client-side (handles legacy data)
    const transactionsRef = collection(db, 'users', userId, 'transactions');
    const txSnapshot = await getDocs(transactionsRef);
    
    // FINLIST FIX: Filter transactions with extended lookback
    // ALL transactions use extended start date to capture transactions added "today"
    // even if the budget period starts later in the month
    const transactionsInPeriod = txSnapshot.docs.filter(doc => {
      const data = doc.data();
      if (data.archived === true) return false;
      
      const txDate = toJSDate(data.date);
      if (!txDate) return false;
      if (txDate > endDate) return false;
      
      // Use extended lookback for ALL transaction types
      // This ensures transactions added "today" count even if budget starts later
      return txDate >= incomeStartDate;
    }).map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Calculate actual income and expenses from transactions
    let incomeActual = 0;
    let expenseActual = 0;
    const expensesByCategory = {};
    
    // ==========================================================================
    // PHASE 2: Build spentByEnvelopeId map for PRIMARY envelope matching
    // This is the canonical source for budget rollup - matches by tx.envelopeId
    // ==========================================================================
    const spentByEnvelopeId = new Map(); // Map<envelopeId, { actual: number, refunds: number }>
    const orphanedEnvelopeIds = new Set(); // Track tx.envelopeId that don't match any envelope
    
    // Build set of valid envelope IDs for this budget
    const validEnvelopeIds = new Set(lines.map(l => l.id).filter(Boolean));
    
    // Problem categories that need extra debugging
    const PROBLEM_CATEGORY_KEYWORDS = ['household', 'leisure', 'housing', 'loan', 'personal-care'];
    
    transactionsInPeriod.forEach(tx => {
      // FINLIST SSOT: Handle refunds with negative amounts
      // Refunds have: amount < 0, status: 'Refunded', isRefund: true, OR type: 'refund'
      const rawAmount = Number(tx.amount) || 0;
      const txType = tx.type || tx.categoryType || 'expense';
      const isRefundTx = tx.status === 'Refunded' || tx.isRefund === true || rawAmount < 0 || txType === 'refund';
      const amount = Math.abs(rawAmount); // Use absolute for calculations
      
      const rawCategoryId = tx.categoryId || tx.category || 'other';
      // CRITICAL: Store under BOTH the normalized AND original categoryId
      // This ensures matching works regardless of envelope's linkedCanonicalCategory format
      const normalizedCategoryId = normalizeCategoryKey(rawCategoryId);
      
      // CUSTOM CATEGORY SUPPORT: Also track by originalCategoryId for custom envelopes
      // This handles cases where tx.categoryId was normalized but original was custom
      const originalCategoryId = tx.originalCategoryId || rawCategoryId;
      
      // Debug: Log transactions for problem categories (with null safety)
      const isProblemCategory = rawCategoryId && normalizedCategoryId && PROBLEM_CATEGORY_KEYWORDS.some(kw => 
        (rawCategoryId?.toLowerCase?.()?.includes(kw)) || (normalizedCategoryId?.toLowerCase?.()?.includes(kw))
      );
      if (import.meta.env?.DEV && isProblemCategory) {
        logger.debug('[BUDGET] 🎯 Problem category transaction found:', {
          description: tx.description,
          rawCategoryId,
          normalizedCategoryId,
          amount,
          type: txType
        });
      }
      
      // Debug: Log insurance-related transactions (with null safety)
      if (rawCategoryId?.toLowerCase?.()?.includes('insur') || normalizedCategoryId?.includes?.('insur')) {
        logger.debug('[BUDGET] Insurance transaction found:', {
          description: tx.description,
          rawCategoryId,
          normalizedCategoryId,
          amount,
          type: txType
        });
      }
      
      if (txType === 'income') {
        incomeActual += amount;
      } else if (txType === 'expense' || txType === 'refund' || txType === undefined) {
        // FINLIST SSOT: Refunds reduce expenseActual, not increase
        if (isRefundTx) {
          expenseActual -= amount; // Refund reduces total expenses
        } else {
          expenseActual += amount;
        }
        
        // ========================================================================
        // PHASE 2: PRIMARY - Aggregate by tx.envelopeId if present
        // This is the canonical matching path for budget rollup
        // ========================================================================
        if (tx.envelopeId) {
          const envId = tx.envelopeId;
          if (!spentByEnvelopeId.has(envId)) {
            spentByEnvelopeId.set(envId, { actual: 0, refunds: 0 });
          }
          const envData = spentByEnvelopeId.get(envId);
          if (isRefundTx) {
            envData.refunds += amount;
          } else {
            envData.actual += amount;
          }
          
          // Track orphaned envelope IDs (tx references envelope not in current budget)
          if (!validEnvelopeIds.has(envId)) {
            orphanedEnvelopeIds.add(envId);
          }
        }
        
        // ========================================================================
        // PHASE 2: FALLBACK - Also track by category for legacy transactions
        // This maintains backward compatibility with transactions without envelopeId
        // ========================================================================
        
        // CRITICAL FIX: Track by BOTH original and normalized category for envelope matching
        // This handles mismatches between envelope linkedCanonicalCategory formats
        const categoriesToTrack = new Set([normalizedCategoryId]);
        
        // Always add the raw category ID for direct matching
        if (rawCategoryId) {
          categoriesToTrack.add(rawCategoryId);
          categoriesToTrack.add(rawCategoryId.toLowerCase());
        }
        
        // CUSTOM ENVELOPE SUPPORT: Add originalCategoryId for custom category matching
        // This ensures transactions linked to custom envelopes match properly
        if (originalCategoryId && originalCategoryId !== rawCategoryId) {
          categoriesToTrack.add(originalCategoryId);
          categoriesToTrack.add(originalCategoryId.toLowerCase());
        }
        
        // Also add lowercase normalized version
        if (normalizedCategoryId) {
          categoriesToTrack.add(normalizedCategoryId.toLowerCase());
        }
        
        for (const catId of categoriesToTrack) {
          if (!catId) continue;
          if (!expensesByCategory[catId]) {
            expensesByCategory[catId] = { actual: 0, refunds: 0 };
          }
          // FINLIST SSOT: Use unified refund detection
          if (isRefundTx) {
            expensesByCategory[catId].refunds += amount;
          } else {
            expensesByCategory[catId].actual += amount;
          }
        }
      }
    });
    
    // Debug: Log all expense categories collected
    if (import.meta.env?.DEV) {
      logger.debug('[BUDGET] 📊 Expense categories collected:', {
        keys: Object.keys(expensesByCategory),
        totals: Object.entries(expensesByCategory).map(([k, v]) => ({ category: k, actual: v.actual }))
      });
      
      // PHASE 2: Log spentByEnvelopeId for debugging
      logger.debug('[BUDGET] 📊 PHASE 2 - Spent by envelopeId:', {
        envelopeCount: spentByEnvelopeId.size,
        totals: Array.from(spentByEnvelopeId.entries()).map(([id, v]) => ({ envelopeId: id, actual: v.actual }))
      });
      
      // Warn once about orphaned envelope IDs
      if (orphanedEnvelopeIds.size > 0) {
        console.warn('[BUDGET] ⚠️ Transactions reference envelopes not in current budget:', Array.from(orphanedEnvelopeIds));
      }
    }
    
    // Update budget lines with actual spending
    const batch = writeBatch(db);
    let expensePlanned = 0;
    let savingsPlanned = 0; // Track savings allocations separately
    let updatedLineCount = 0;
    
    for (const line of lines) {
      // Skip lines without id (shouldn't happen, but protect against corrupted data)
      if (!line.id) {
        logger.warn('[BUDGET SERVICE] Skipping line with null id:', line);
        continue;
      }
      
      // Compute category matching IDs upfront (used for both matching and backfill)
      const matchingCategoryId = line.linkedCanonicalCategory || normalizeCategoryKey(line.categoryId) || line.categoryId;
      const lowerCategoryId = (line.categoryId || '').toLowerCase();
      const normalizedLower = normalizeCategoryKey(lowerCategoryId) || lowerCategoryId;
      const directCategoryId = line.categoryId;
      
      // ========================================================================
      // PHASE 2: PRIMARY - Check spentByEnvelopeId FIRST
      // This is the canonical matching path - uses line.id as the envelope ID
      // ========================================================================
      let categoryData = null;
      let matchSource = 'none';
      
      // Try envelopeId matching first (PRIMARY)
      if (spentByEnvelopeId.has(line.id)) {
        categoryData = spentByEnvelopeId.get(line.id);
        matchSource = 'envelopeId';
      }
      
      // ========================================================================
      // PHASE 2: FALLBACK - Use category matching for legacy transactions
      // Only used if no envelopeId match found
      // ========================================================================
      if (!categoryData || (categoryData.actual === 0 && categoryData.refunds === 0)) {
        // Try various category matching strategies
        const catData = expensesByCategory[matchingCategoryId] || 
                        expensesByCategory[normalizedLower] ||
                        expensesByCategory[directCategoryId] ||
                        expensesByCategory[line.categoryId] || 
                        expensesByCategory[lowerCategoryId];
        
        if (catData) {
          // If we had envelopeId data but it was 0, merge with category data
          if (categoryData && matchSource === 'envelopeId') {
            categoryData = {
              actual: categoryData.actual + catData.actual,
              refunds: categoryData.refunds + catData.refunds
            };
            matchSource = 'envelopeId+category';
          } else {
            categoryData = catData;
            matchSource = 'category';
          }
        }
      }
      
      // Default if no match found
      if (!categoryData) {
        categoryData = { actual: 0, refunds: 0 };
      }
      
      // PHASE 2: Debug logging for envelope matching
      if (import.meta.env?.DEV && line.isCustomCategory) {
        logger.debug(`[BUDGET] 🔍 Custom envelope match for "${line.categoryId}":`, {
          envelopeId: line.id,
          matchSource,
          actual: categoryData.actual,
          refunds: categoryData.refunds
        });
      }
      
      const planned = Number(line.planned) || 0;
      const carryIn = Number(line.carryIn) || 0;
      
      // =========================================================================
      // FINLIST FIX: SAVINGS ENVELOPES vs EXPENSE ENVELOPES
      // Savings envelopes (debt-paydown, emergency-fund, etc.) track FUNDING,
      // NOT spending from transactions. Preserve fundedThisMonth as actual.
      // =========================================================================
      const isSavingsLine = line.isSavingsEnvelope || isSavingsCategory(line.categoryId);
      
      // For savings envelopes: use fundedThisMonth (manual contributions)
      // For expense envelopes: use transaction-based actual spending
      const newActual = isSavingsLine 
        ? (Number(line.fundedThisMonth) || Number(line.actual) || 0)
        : categoryData.actual;
      const newRefunds = isSavingsLine ? 0 : categoryData.refunds;
      const newRemaining = planned - (newActual - newRefunds) + carryIn;
      
      // FINLIST FIX: Separate savings from expense allocations
      // Savings categories (emergency-fund, debt-repayment, etc.) should be tracked separately
      // so that the 22% savings allocation is properly reflected
      if (isSavingsLine) {
        savingsPlanned += planned;
      } else {
        expensePlanned += planned;
      }
      
      // Always update line to ensure values are synced
      const lineRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', line.id);
      
      // For savings envelopes: preserve fundedThisMonth and don't overwrite with transaction data
      // For expense envelopes: update actual from transactions
      const lineUpdates = {
        actual: newActual,
        remaining: newRemaining,
        // Backfill linkedCanonicalCategory if missing
        linkedCanonicalCategory: line.linkedCanonicalCategory || matchingCategoryId
      };
      
      // Only update refunds for expense envelopes
      if (!isSavingsLine) {
        lineUpdates.refunds = newRefunds;
      }
      
      batch.update(lineRef, lineUpdates);
      updatedLineCount++;
    }
    
    // Commit line updates
    await batch.commit();
    
    // FINLIST FIX: For custom budgets with no lines, use stored values
    let finalExpensePlanned = expensePlanned;
    let finalIncomePlanned = 0;
    
    if (isCustomBudget) {
      // Use stored budget amount if no lines have planned amounts
      if (finalExpensePlanned === 0 && storedBudgetAmount > 0) {
        finalExpensePlanned = storedBudgetAmount;
        logger.debug(`[RECALC] Using stored customBudgetAmount for planned expenses: ${storedBudgetAmount}`);
      }
      // Use stored monthly income
      if (storedMonthlyIncome > 0) {
        finalIncomePlanned = storedMonthlyIncome;
        logger.debug(`[RECALC] Using stored monthlyIncome for planned income: ${storedMonthlyIncome}`);
      }
    }
    
    // FINLIST FIX: For custom budgets, calculate auto-savings from income - budget amount
    // Auto-savings = monthlyIncome - customBudgetAmount (what's left after budget allocation)
    let autoSavingsPlanned = 0;
    if (isCustomBudget && storedMonthlyIncome > 0 && storedBudgetAmount > 0) {
      autoSavingsPlanned = Math.max(0, storedMonthlyIncome - storedBudgetAmount);
      logger.debug(`[RECALC] Custom budget auto-savings: ${autoSavingsPlanned} (${storedMonthlyIncome} - ${storedBudgetAmount})`);
    }

    const summary = {
      incomePlanned: finalIncomePlanned,
      incomeActual,
      expensePlanned: finalExpensePlanned,
      savingsPlanned: isCustomBudget && autoSavingsPlanned > 0 ? autoSavingsPlanned : savingsPlanned,
      expenseActual,
      safeToSpend: 0,
      burnRatePct: 0,
      projectedEOM: 0,
      alerts: [],
      lastCalculated: Timestamp.now()
    };

    // Calculate safe-to-spend
    // FINLIST FIX: For Custom budgets, Safe-to-Spend = Budget Amount - Actual Expenses
    // For other budgets, Safe-to-Spend = Income - Expenses - Planned Savings
    if (isCustomBudget && storedBudgetAmount > 0) {
      // Custom budget: safe to spend is budget amount minus what you've spent
      summary.safeToSpend = storedBudgetAmount - expenseActual;
    } else {
      // Other budgets: safe to spend is income minus expenses minus savings allocations
      summary.safeToSpend = incomeActual - expenseActual - savingsPlanned;
    }

    // Calculate burn rate
    const now = new Date();
    const totalDays = Math.max(1, (endDate - startDate) / (1000 * 60 * 60 * 24));
    const elapsedDays = Math.max(0, Math.min(totalDays, (now - startDate) / (1000 * 60 * 60 * 24)));
    const elapsedRatio = elapsedDays / totalDays;

    if (summary.expensePlanned > 0 && elapsedRatio > 0) {
      summary.burnRatePct = (summary.expenseActual / (summary.expensePlanned * elapsedRatio)) * 100;
    }

    // Project end-of-month spending
    if (elapsedRatio > 0 && elapsedRatio < 1) {
      summary.projectedEOM = summary.expenseActual / elapsedRatio;
    } else {
      summary.projectedEOM = summary.expenseActual;
    }

    // Helper to get category display name
    const getCategoryName = (categoryId) => {
      // Try taxonomy categories first (budget envelopes use these)
      const taxonomyCat = getTaxonomyCategory(categoryId, CATEGORY_TYPES.EXPENSE);
      if (taxonomyCat) return taxonomyCat.name;
      
      // Try unified categories
      const unifiedCat = findCategoryById(categoryId);
      if (unifiedCat) return unifiedCat.name;
      
      // Fallback: format the ID nicely
      return categoryId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    // Generate alerts based on updated line data
    for (const line of lines) {
      const categoryData = expensesByCategory[line.categoryId] || { actual: 0, refunds: 0 };
      const planned = Number(line.planned) || 0;
      const actualSpent = categoryData.actual - categoryData.refunds;
      const percentUsed = planned > 0 ? (actualSpent / planned) * 100 : 0;
      const categoryName = getCategoryName(line.categoryId);

      if (percentUsed >= 90 && percentUsed < 100) {
        summary.alerts.push({
          type: 'high_usage',
          message: `${categoryName} is at ${percentUsed.toFixed(0)}% of budget`,
          categoryId: line.categoryId,
          categoryName,
          severity: 'warn',
          percentUsed: percentUsed.toFixed(0)
        });
      } else if (percentUsed >= 100) {
        summary.alerts.push({
          type: 'over_budget',
          message: `${categoryName} exceeded budget by ${(percentUsed - 100).toFixed(0)}% (${percentUsed.toFixed(0)}% used)`,
          categoryId: line.categoryId,
          categoryName,
          severity: 'crit',
          percentUsed: percentUsed.toFixed(0)
        });
      }
    }

    if (summary.burnRatePct > 110) {
      // Only show burn rate alert after first week of budget period (to avoid false alarms from bills paid early)
      const daysIntobudget = elapsedDays;
      if (daysIntobudget >= 7) {
        summary.alerts.push({
          type: 'high_burn_rate',
          message: `Spending pace is high – you've used ${((summary.expenseActual / summary.expensePlanned) * 100).toFixed(0)}% of budget with ${((1 - elapsedRatio) * 100).toFixed(0)}% of the period remaining`,
          severity: 'warn'
        });
      }
    }

    // Save summary - use setDoc with merge:false to completely overwrite any stale data
    const summaryRef = getBudgetSummaryRef(userId, budgetId);
    await setDoc(summaryRef, summary, { merge: false });

    if (import.meta.env.DEV) {
      logger.debug('[BUDGET SERVICE] Summary recalculated and saved:', {
        budgetId,
        incomeActual,
        expenseActual,
        expensePlanned,
        savingsPlanned,
        safeToSpend: summary.safeToSpend,
        transactionCount: transactionsInPeriod.length,
        categoriesWithSpending: Object.keys(expensesByCategory).length,
        writtenToFirestore: true
      });
      
      // Verify what was written by reading it back immediately
      const verifyDoc = await getDoc(summaryRef);
      if (verifyDoc.exists()) {
        const verifiedData = verifyDoc.data();
        logger.debug('[BUDGET SERVICE] Verification read from Firestore:', {
          incomeActual: verifiedData.incomeActual,
          expenseActual: verifiedData.expenseActual,
          savingsPlanned: verifiedData.savingsPlanned,
          safeToSpend: verifiedData.safeToSpend,
          match: verifiedData.incomeActual === incomeActual && verifiedData.safeToSpend === summary.safeToSpend
        });
      }
    }

    return summary;
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error recalculating summary:', error);
    throw error;
  }
}

/**
 * Force clear and recalculate budget summary
 * Use this to fix any data corruption issues
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {Promise<object>}
 */
export async function forceClearAndRecalc(userId, budgetId) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');

    logger.debug('[BUDGET SERVICE] FORCE CLEAR: Deleting old summary and recalculating fresh...');
    
    // Delete the old summary document completely
    const summaryRef = getBudgetSummaryRef(userId, budgetId);
    await deleteDoc(summaryRef);
    
    logger.debug('[BUDGET SERVICE] FORCE CLEAR: Old summary deleted, recalculating...');
    
    // Recalculate fresh
    const newSummary = await recalcBudgetSummary(userId, budgetId);
    
    logger.debug('[BUDGET SERVICE] FORCE CLEAR: Complete! New summary:', newSummary);
    
    return newSummary;
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error in force clear and recalc:', error);
    throw error;
  }
}

/**
 * DEPRECATED - Legacy budget migration (v1 → v2)
 * This function is no longer needed as all budgets should now be in v2 format.
 * Kept for reference only.
 * @deprecated
 */
export async function migrateLegacyBudget(userId, legacyBudgetId) {
  logger.warn('[BUDGET MIGRATION] DEPRECATED: Legacy migration function called. All budgets should be in v2 format.');
  throw new Error('Legacy budget migration is no longer supported. Please use v2 budget APIs.');
}

/**
 * DEPRECATED - Legacy budget auto-migration (v1 → v2)
 * This function is no longer needed as all budgets should now be in v2 format.
 * @deprecated
 */
export async function autoMigrateLegacyBudgets(userId) {
  logger.warn('[BUDGET MIGRATION] DEPRECATED: Legacy auto-migration called. All budgets should be in v2 format.');
  return [];
}

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * SMART BUDGET HUB - AGGREGATION FUNCTIONS
 * Phase 1 Implementation: Data layer for enhanced Budget Dashboard
 * ════════════════════════════════════════════════════════════════════════════════
 */

/**
 * @typedef {Object} BudgetHubMetrics
 * @property {number} safeToSpend - Available to spend (netAfterSavings)
 * @property {number} actualIncome - Total income received this period
 * @property {number} plannedIncome - Total income expected this period
 * @property {number} actualExpenses - Total expenses this period
 * @property {number} plannedExpenses - Total budgeted expenses
 * @property {number} totalSavings - Total saved this period (goal contributions)
 * @property {number} savingRate - Savings rate as percentage (0-100)
 * @property {number} surplusOrDeficit - Income minus expenses minus savings
 * @property {Object} periodInfo - Period date range info
 */

/**
 * @typedef {Object} TopCategory
 * @property {string} categoryId - Category identifier
 * @property {string} categoryName - Display name
 * @property {number} amount - Total amount
 * @property {number} percentage - Percentage of total
 */

/**
 * @typedef {Object} GoalsSummary
 * @property {number} totalGoalContributions - Total contributed to goals this period
 * @property {Object|null} primaryGoal - Most active or nearest-due goal
 */

/**
 * Get comprehensive budget hub metrics for the Smart Budget Dashboard
 * Aggregates data from budget, transactions, and savings into a single object
 * 
 * @param {string} userId - User ID
 * @param {string} budgetId - Budget ID
 * @param {Object} savingsSummary - Savings summary from insights API (optional)
 * @returns {Promise<BudgetHubMetrics>}
 */
export async function getBudgetHubMetrics(userId, budgetId, savingsSummary = null) {
  try {
    if (!userId) throw new Error('User ID required');
    if (!budgetId) throw new Error('Budget ID required');

    // Helper to normalize Firestore/string dates to JS Date
    const toJSDate = (dateVal) => {
      if (!dateVal) return null;
      if (dateVal.toDate) return dateVal.toDate();
      if (typeof dateVal === 'string') return new Date(dateVal);
      if (dateVal instanceof Date) return dateVal;
      return null;
    };

    // Helper to convert dollars to cents (integer) to avoid floating-point errors
    const toCents = (amount) => Math.round(Number(amount || 0) * 100);
    // Helper to convert cents back to dollars
    const toDollars = (cents) => cents / 100;

    // Fetch budget with lines and summary
    const budget = await _getBudget(userId, budgetId);
    if (!budget) throw new Error('Budget not found');

    const lines = budget.lines || [];
    const summary = budget.summary || {};
    
    // FINLIST FIX: Check if this is a custom budget with stored income/budget values
    const isCustomBudget = budget.template === 'CUSTOM';
    const storedMonthlyIncome = budget.monthlyIncome || 0;
    const storedBudgetAmount = budget.customBudgetAmount || 0;

    // Calculate planned totals from budget lines (in cents)
    // FINLIST FIX: Separate savings and expense planned amounts
    // Also track funded savings (from Fund button contributions)
    let plannedExpensesCents = 0;
    let plannedSavingsCents = 0;
    let fundedSavingsCents = 0; // Track savings already funded via Fund button
    lines.forEach(line => {
      const plannedCents = toCents(line.planned);
      if (isSavingsCategory(line.categoryId)) {
        plannedSavingsCents += plannedCents;
        // fundedThisMonth is stored in pounds, convert to cents
        const fundedCents = toCents(line.fundedThisMonth || 0);
        fundedSavingsCents += fundedCents;
      } else {
        plannedExpensesCents += plannedCents;
      }
    });
    
    // FINLIST FIX: For custom budgets with no lines, use stored budget amount
    if (isCustomBudget && plannedExpensesCents === 0 && storedBudgetAmount > 0) {
      plannedExpensesCents = toCents(storedBudgetAmount);
      logger.debug(`[METRICS] Using stored customBudgetAmount for planned expenses: ${storedBudgetAmount}`);
    }

    // Get planned income from summary, or use stored monthly income for custom budgets
    let plannedIncomeCents = toCents(summary.incomePlanned);
    
    // FINLIST FIX: For custom budgets, use stored monthly income if summary is empty
    if (isCustomBudget && plannedIncomeCents === 0 && storedMonthlyIncome > 0) {
      plannedIncomeCents = toCents(storedMonthlyIncome);
      logger.debug(`[METRICS] Using stored monthlyIncome for planned income: ${storedMonthlyIncome}`);
    }

    // Period dates for querying transactions
    const startDate = budget.startDate?.toDate 
      ? budget.startDate.toDate() 
      : (budget.startDate ? new Date(budget.startDate) : new Date());
    const endDate = budget.endDate?.toDate 
      ? budget.endDate.toDate() 
      : (budget.endDate ? new Date(budget.endDate) : new Date());

    // FINLIST FIX: Calculate income lookback start date
    // For income, look back to the earlier of: budget start OR start of current calendar month
    // This ensures income added "today" is always counted even if budget starts later
    const currentDate = new Date();
    const startOfCurrentMonth = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1));
    const incomeStartDate = startDate < startOfCurrentMonth ? startDate : startOfCurrentMonth;
    
    logger.debug(`[METRICS REBUILD] Income lookback extended: ${incomeStartDate.toISOString()} (budget start: ${startDate.toISOString()}, month start: ${startOfCurrentMonth.toISOString()})`);

    // ============================================================
    // CLEAN REBUILD: Aggregate transactions using INTEGER CENTS
    // This eliminates all floating-point precision issues
    // ============================================================
    let totalIncomeCents = 0;
    let totalExpensesCents = 0;
    let totalSavingsCents = 0;
    
    // Track transactions for debugging
    const incomeTransactions = [];
    const expenseTransactions = [];
    const savingsTransactions = [];
    
    try {
      const transactionsRef = collection(db, 'users', userId, 'transactions');
      const txSnapshot = await getDocs(transactionsRef);

      logger.debug(`[METRICS REBUILD] Processing ${txSnapshot.docs.length} total transactions...`);
      logger.debug(`[METRICS REBUILD] Budget period: ${startDate.toISOString()} to ${endDate.toISOString()}`);

      txSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const docId = doc.id;

        // Skip archived transactions
        if (data.archived === true) {
          return;
        }

        // Check date is within budget period
        const txDate = toJSDate(data.date);
        if (!txDate) {
          return;
        }
        
        // FINLIST FIX: Use extended lookback for ALL transactions
        // This ensures transactions added "today" count even if the budget 
        // period starts later in the month (e.g., budget starts Dec 31 but today is Dec 27)
        if (txDate < incomeStartDate || txDate > endDate) {
          return;
        }
        
        // Determine transaction type for categorization
        const txType = (data.type || data.categoryType || 'expense').toLowerCase();

        // Get raw amount (preserve sign for refunds)
        const rawAmount = Number(data.amount) || 0;
        
        // Check if this is a refund transaction
        const isRefundTx = data.isRefund === true || data.status === 'Refunded';
        
        // For refunds, use the raw amount (which is negative) to reduce totals
        // For regular transactions, use absolute value
        const amountCents = isRefundTx ? toCents(rawAmount) : Math.abs(toCents(rawAmount));
        
        // 🔍 DETAILED DEBUG: Log every transaction's raw fields
        logger.debug('🔍 HUB TX RAW:', {
          id: docId,
          'data.type': data.type,
          'data.categoryType': data.categoryType,
          resolvedType: txType,
          rawAmount: rawAmount,
          amountCents: amountCents,
          isRefund: isRefundTx,
          description: data.description || data.note || 'N/A',
          categoryId: data.categoryId
        });

        // Categorize and accumulate
        if (txType === 'income') {
          totalIncomeCents += Math.abs(amountCents); // Income always positive
          incomeTransactions.push({
            id: docId,
            amount: rawAmount,
            amountCents: Math.abs(amountCents),
            description: data.description || data.note || 'No description',
            date: txDate.toISOString(),
            categoryId: data.categoryId
          });
        } else if (txType === 'expense') {
          // For expenses: add positive amounts, subtract refunds (negative amounts)
          totalExpensesCents += amountCents;
          expenseTransactions.push({
            id: docId,
            amount: rawAmount,
            amountCents,
            isRefund: isRefundTx,
            description: data.description || data.note || 'No description',
            date: txDate.toISOString(),
            categoryId: data.categoryId
          });
        } else if (txType === 'savings') {
          // Only count deposits, not withdrawals
          if (!data.meta?.isWithdrawal) {
            totalSavingsCents += amountCents;
            savingsTransactions.push({
              id: docId,
              amount: rawAmount,
              amountCents,
              description: data.description || data.note || 'No description',
              date: txDate.toISOString()
            });
          }
        }
      });

      // ============================================================
      // DEBUG OUTPUT - Detailed transaction breakdown
      // ============================================================
      logger.debug('[METRICS REBUILD] ========== INCOME TRANSACTIONS ==========');
      incomeTransactions.forEach((tx, i) => {
        logger.debug(`  ${i + 1}. [${tx.id}] $${tx.amount} (${tx.amountCents} cents) - "${tx.description}" - ${tx.date}`);
      });
      logger.debug(`[METRICS REBUILD] INCOME TOTAL: ${totalIncomeCents} cents = $${toDollars(totalIncomeCents)}`);
      
      logger.debug('[METRICS REBUILD] ========== EXPENSE TRANSACTIONS ==========');
      expenseTransactions.forEach((tx, i) => {
        logger.debug(`  ${i + 1}. [${tx.id}] $${tx.amount} (${tx.amountCents} cents) - "${tx.description}" - ${tx.date}`);
      });
      logger.debug(`[METRICS REBUILD] EXPENSES TOTAL: ${totalExpensesCents} cents = $${toDollars(totalExpensesCents)}`);
      
      logger.debug('[METRICS REBUILD] ========== SAVINGS TRANSACTIONS ==========');
      savingsTransactions.forEach((tx, i) => {
        logger.debug(`  ${i + 1}. [${tx.id}] $${tx.amount} (${tx.amountCents} cents) - "${tx.description}" - ${tx.date}`);
      });
      logger.debug(`[METRICS REBUILD] SAVINGS TOTAL: ${totalSavingsCents} cents = $${toDollars(totalSavingsCents)}`);

    } catch (err) {
      logger.error('[METRICS REBUILD] Error querying transactions:', err);
      // Fallback to summary values
      totalIncomeCents = toCents(summary.incomeActual);
      totalExpensesCents = toCents(summary.expenseActual);
      totalSavingsCents = toCents(savingsSummary?.goalContributionsTotal || savingsSummary?.savedIntoGoals || 0);
    }

    // ============================================================
    // CALCULATE FINAL METRICS (all in cents, then convert)
    // ============================================================
    
    // If no planned income was set, use actual income as planned
    if (plannedIncomeCents === 0 && totalIncomeCents > 0) {
      plannedIncomeCents = totalIncomeCents;
    }

    // FINLIST FIX: For Custom Budgets, calculate auto-savings and safe-to-spend differently
    // Auto-savings = monthlyIncome - customBudgetAmount (what's left after budget allocation)
    // Safe-to-spend = customBudgetAmount - actualExpenses (your spending limit minus what you've spent)
    let autoSavingsCents = 0;
    let safeToSpendCents;
    
    if (isCustomBudget && storedBudgetAmount > 0) {
      // Calculate auto-savings: income - budget = savings
      autoSavingsCents = Math.max(0, toCents(storedMonthlyIncome) - toCents(storedBudgetAmount));
      
      // For custom budget, planned savings is the auto-savings (if no explicit savings lines)
      if (plannedSavingsCents === 0 && autoSavingsCents > 0) {
        plannedSavingsCents = autoSavingsCents;
      }
      
      // Safe to spend = budget amount - actual expenses
      safeToSpendCents = toCents(storedBudgetAmount) - totalExpensesCents;
      
      logger.debug(`[METRICS] Custom budget: autoSavings=${autoSavingsCents/100}, safeToSpend=${safeToSpendCents/100}`);
    } else {
      // Standard calculation for other budget types
      // Safe to Spend = Income - Expenses - Actual Savings - Planned Savings
      const effectiveSavingsCents = Math.max(totalSavingsCents, plannedSavingsCents);
      safeToSpendCents = totalIncomeCents - totalExpensesCents - effectiveSavingsCents;
    }
    
    // For non-custom budgets, calculate effective savings as before
    const effectiveSavingsCents = Math.max(totalSavingsCents, plannedSavingsCents);
    
    // FINLIST CFO Model: Remaining savings = Planned - Funded
    // This shows how much still needs to be funded this period
    const remainingSavingsCents = Math.max(0, plannedSavingsCents - fundedSavingsCents);
    
    // Convert all values back to dollars
    const actualIncome = toDollars(totalIncomeCents);
    const plannedIncome = toDollars(plannedIncomeCents);
    const actualExpenses = toDollars(totalExpensesCents);
    const plannedExpenses = toDollars(plannedExpensesCents);
    const plannedSavings = toDollars(plannedSavingsCents);
    const fundedSavings = toDollars(fundedSavingsCents);
    const remainingSavings = toDollars(remainingSavingsCents); // NEW: Shows what's left to fund
    const totalSavings = toDollars(Math.max(totalSavingsCents, plannedSavingsCents)); // Use higher of actual vs planned
    const safeToSpend = toDollars(safeToSpendCents);

    // Savings rate: % of income going to savings
    // Based on REMAINING savings (decreases as you fund)
    const savingRate = actualIncome > 0 
      ? Math.round((remainingSavings / actualIncome) * 100 * 10) / 10 
      : 0;

    // Surplus/Deficit (same as safeToSpend)
    const surplusOrDeficit = safeToSpend;

    // ============================================================
    // FINAL DEBUG OUTPUT
    // ============================================================
    logger.debug('[METRICS REBUILD] ========== FINAL CALCULATION ==========');
    logger.debug(`[METRICS REBUILD] Income:   ${totalIncomeCents} cents = $${actualIncome}`);
    logger.debug(`[METRICS REBUILD] Expenses: ${totalExpensesCents} cents = $${actualExpenses}`);
    logger.debug(`[METRICS REBUILD] Actual Savings:  ${totalSavingsCents} cents = $${toDollars(totalSavingsCents)}`);
    logger.debug(`[METRICS REBUILD] Planned Savings: ${plannedSavingsCents} cents = $${plannedSavings}`);
    logger.debug(`[METRICS REBUILD] Funded Savings:  ${fundedSavingsCents} cents = $${fundedSavings}`);
    logger.debug(`[METRICS REBUILD] Remaining Savings: ${remainingSavingsCents} cents = $${remainingSavings}`);
    logger.debug(`[METRICS REBUILD] Effective Savings (max): ${effectiveSavingsCents} cents = $${totalSavings}`);
    logger.debug(`[METRICS REBUILD] Formula:  ${totalIncomeCents} - ${totalExpensesCents} - ${effectiveSavingsCents} = ${safeToSpendCents} cents`);
    logger.debug(`[METRICS REBUILD] Safe to Spend: $${safeToSpend}`);
    logger.debug('[METRICS REBUILD] =====================================');

    const metrics = {
      safeToSpend,
      actualIncome,
      plannedIncome,
      actualExpenses,
      plannedExpenses,
      plannedSavings, // Total planned savings allocations
      fundedSavings,  // NEW: Amount already funded via Fund button
      remainingSavings, // NEW: Planned - Funded (what's left to save)
      totalSavings,
      savingRate,
      surplusOrDeficit,
      periodInfo: {
        startDate,
        endDate,
        cadence: budget.cadence || 'MONTHLY',
        status: budget.status || 'OPEN'
      },
      // Include raw summary for backward compatibility
      rawSummary: summary,
      // Include debug info
      _debug: {
        incomeTransactionCount: incomeTransactions.length,
        expenseTransactionCount: expenseTransactions.length,
        savingsTransactionCount: savingsTransactions.length,
        totalIncomeCents,
        totalExpensesCents,
        totalSavingsCents,
        plannedSavingsCents,
        fundedSavingsCents,
        remainingSavingsCents,
        effectiveSavingsCents,
        safeToSpendCents
      }
    };

    // Dev logging
    if (import.meta.env.DEV) {
      logger.debug('[BUDGET] metrics loaded', {
        budgetId,
        safeToSpend,
        actualIncome,
        plannedIncome,
        actualExpenses,
        plannedExpenses,
        plannedSavings,
        fundedSavings,
        remainingSavings,
        totalSavings,
        savingRate: `${savingRate}%`,
        surplusOrDeficit
      });
    }

    return metrics;
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error getting budget hub metrics:', error);
    throw error;
  }
}

// ============================================================================
// SAVINGS ENVELOPE HELPERS (CFO Money Flow Model)
// ============================================================================

/**
 * Get all savings envelopes from a budget
 * These are allocations like Emergency Fund, Debt Repayment, etc.
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {Promise<Array>} Array of savings envelope lines
 */
export async function getSavingsEnvelopes(userId, budgetId) {
  try {
    if (!userId || !budgetId) return [];
    
    const linesRef = getBudgetLinesRef(userId, budgetId);
    const snapshot = await getDocs(linesRef);
    
    const savingsEnvelopes = [];
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      // Check if this is a savings category
      if (data.isSavingsEnvelope || 
          isSavingsCategory(data.categoryId) || 
          isSavingsCategory(data.normalizedCategoryId)) {
        savingsEnvelopes.push({
          id: doc.id,
          ...data,
          isSavingsEnvelope: true // Ensure flag is set
        });
      }
    });
    
    logger.debug('[BUDGET SERVICE] Found savings envelopes:', savingsEnvelopes.length);
    return savingsEnvelopes;
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error getting savings envelopes:', error);
    return [];
  }
}

/**
 * Get savings funding summary for a budget
 * Shows how much of planned savings has actually been funded
 * @param {string} userId 
 * @param {string} budgetId 
 * @returns {Promise<Object>} { planned, funded, remaining, envelopes[] }
 */
export async function getSavingsFundingSummary(userId, budgetId) {
  try {
    const savingsEnvelopes = await getSavingsEnvelopes(userId, budgetId);
    
    let totalPlanned = 0;
    let totalFunded = 0;
    
    const envelopesWithStatus = savingsEnvelopes.map(env => {
      const planned = env.planned || 0;
      const funded = env.fundedThisMonth || env.actual || 0;
      
      totalPlanned += planned;
      totalFunded += funded;
      
      return {
        ...env,
        planned,
        funded,
        remaining: Math.max(0, planned - funded),
        percentFunded: planned > 0 ? Math.round((funded / planned) * 100) : 0
      };
    });
    
    return {
      totalPlanned,
      totalFunded,
      totalRemaining: Math.max(0, totalPlanned - totalFunded),
      percentFunded: totalPlanned > 0 ? Math.round((totalFunded / totalPlanned) * 100) : 0,
      envelopes: envelopesWithStatus
    };
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error getting savings funding summary:', error);
    return {
      totalPlanned: 0,
      totalFunded: 0,
      totalRemaining: 0,
      percentFunded: 0,
      envelopes: []
    };
  }
}

/**
 * Fund a savings envelope (record a contribution)
 * FINLIST CFO MODEL: Updates budget envelope AND contributes to linked Savings Goal
 * This creates a unified flow where:
 * - Budget tracks monthly allocation targets
 * - Goals track overall progress toward targets
 * - Both are synchronized when funding
 * 
 * @param {string} userId 
 * @param {string} budgetId 
 * @param {string} lineId - The savings envelope line ID
 * @param {number} amount - Amount to fund (in currency units, not cents)
 * @param {object} options - { skipGoalSync: boolean }
 * @returns {Promise<Object>} Updated envelope data with goal sync result
 */
export async function fundSavingsEnvelope(userId, budgetId, lineId, amount, options = {}) {
  try {
    if (!userId || !budgetId || !lineId) {
      throw new Error('Missing required parameters');
    }
    
    // NOTE: Savings envelopes store amounts in POUNDS (matching template creation)
    // NOT in cents like expense transactions
    const amountPounds = parseFloat(amount);
    const lineRef = doc(db, 'users', userId, 'budgets', budgetId, 'lines', lineId);
    const lineDoc = await getDoc(lineRef);
    
    if (!lineDoc.exists()) {
      throw new Error('Savings envelope not found');
    }
    
    const lineData = lineDoc.data();
    const currentFunded = lineData.fundedThisMonth || 0;
    const newFunded = currentFunded + amountPounds;
    
    const updates = {
      fundedThisMonth: newFunded,
      actual: newFunded, // Also update actual for consistency
      remaining: Math.max(0, (lineData.planned || 0) - newFunded),
      updatedAt: Timestamp.now()
    };
    
    await updateDoc(lineRef, updates);
    
    // FINLIST CFO MODEL: Sync with linked Savings Goal
    let goalSyncResult = null;
    if (!options.skipGoalSync) {
      try {
        // Dynamic import to avoid circular dependencies
        const { findGoalByLinkedEnvelope, addContribution, getGoalById } = await import('./goalsService.js');
        
        let linkedGoal = null;
        
        // Priority 1: Use linkedGoalId if envelope has a direct goal link
        if (lineData.linkedGoalId) {
          try {
            linkedGoal = await getGoalById(userId, lineData.linkedGoalId);
            logger.debug('[BUDGET SERVICE] Found goal via linkedGoalId:', linkedGoal?.name);
          } catch (e) {
            logger.debug('[BUDGET SERVICE] linkedGoalId goal not found, trying other methods');
          }
        }
        
        // Priority 2: Find goal by envelope ID, name, or category
        if (!linkedGoal) {
          linkedGoal = await findGoalByLinkedEnvelope(userId, {
            linkedEnvelopeId: lineId,
            name: lineData.categoryName || lineData.name,
            categoryId: lineData.categoryId
          });
        }
        
        if (linkedGoal) {
          // Contribute to the linked goal
          const updatedGoal = await addContribution(
            userId,
            linkedGoal.id,
            amountPounds,
            `Budget funding: ${lineData.categoryName || 'Savings'}`,
            { fromBudget: true, budgetId, lineId }
          );
          
          goalSyncResult = {
            synced: true,
            goalId: linkedGoal.id,
            goalName: linkedGoal.name,
            newGoalAmount: updatedGoal.currentAmount,
            goalProgress: Math.round((updatedGoal.currentAmount / updatedGoal.targetAmount) * 100)
          };
          
          logger.debug('[BUDGET SERVICE] Synced funding to goal:', goalSyncResult);
        } else {
          goalSyncResult = { synced: false, reason: 'No matching goal found' };
          logger.debug('[BUDGET SERVICE] No linked goal found for envelope:', lineData.categoryName);
        }
      } catch (goalError) {
        logger.warn('[BUDGET SERVICE] Goal sync failed (non-blocking):', goalError.message);
        goalSyncResult = { synced: false, reason: goalError.message };
      }
    }
    
    // Recalculate budget summary
    await recalcBudgetSummary(userId, budgetId);
    
    logger.debug('[BUDGET SERVICE] Funded savings envelope:', {
      lineId,
      amount: amountPounds,
      newFunded,
      goalSync: goalSyncResult
    });
    
    return {
      id: lineId,
      ...lineData,
      ...updates,
      goalSync: goalSyncResult
    };
  } catch (error) {
    logger.error('[BUDGET SERVICE] Error funding savings envelope:', error);
    throw error;
  }
}

// getTopIncomeSources - MOVED to budgetAnalytics.js
// getTopSpendingCategories - MOVED to budgetAnalytics.js
// getGoalsSummary - MOVED to budgetAnalytics.js
